import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import DebouncedOrderSearch from "../components/DebouncedOrderSearch";
import { Contact } from "../orderEntry/types/Contact";
import { ContactOption } from "../phonebook/phonebookAutocomplete/ContactOption";
import { UserContext } from "../contexts/UserContext";
import { useContext } from "react";
import { prependUserToSelectNote } from "../utils/prependUserToSelectNote";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

export default function AddSurveyAndZoningModal({ isOpen, onClose, onSaved }: Props) {
  const { user } = useContext(UserContext);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState("");
  const [includeSurvey, setIncludeSurvey] = useState(false);
  const [includeZoningReport, setIncludeZoningReport] = useState(false);
  const [surveyContacts, setSurveyContacts] = useState<Contact[]>([]);
  const [surveyContactOptions, setSurveyContactOptions] = useState<Contact[]>([]);
  const [surveyContactSearch, setSurveyContactSearch] = useState("");
  const [isLoadingSurveyContacts, setIsLoadingSurveyContacts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const isSaveDisabled = useMemo(
    () => !selectedOrderNumber || (!includeSurvey && !includeZoningReport) || isSubmitting,
    [includeSurvey, includeZoningReport, isSubmitting, selectedOrderNumber]
  );

  const resetState = () => {
    setSelectedOrderNumber("");
    setIncludeSurvey(false);
    setIncludeZoningReport(false);
    setSurveyContacts([]);
    setSurveyContactOptions([]);
    setSurveyContactSearch("");
    setIsLoadingSurveyContacts(false);
    setIsSubmitting(false);
    setErrorMessage("");
  };

  const closeModal = () => {
    resetState();
    onClose();
  };

  const handleClose = () => {
    if (isSubmitting) return;
    closeModal();
  };

  useEffect(() => {
    if (!includeSurvey || !surveyContactSearch.trim()) {
      setSurveyContactOptions([]);
      setIsLoadingSurveyContacts(false);
      return;
    }

    const abortController = new AbortController();
    const timer = window.setTimeout(async () => {
      setIsLoadingSurveyContacts(true);
      try {
        const { data } = await axios.get<Contact[]>("/api/clientphonebook/contacts/getOrderEntryContacts", {
          params: { searchText: surveyContactSearch, contactType: "Surveyor" },
          signal: abortController.signal
        });
        setSurveyContactOptions((Array.isArray(data) ? data : []).filter((contact) => contact.isIndividual));
      } catch (error) {
        if (!axios.isAxiosError(error) || error.code !== "ERR_CANCELED") {
          setSurveyContactOptions([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingSurveyContacts(false);
        }
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
      abortController.abort();
    };
  }, [includeSurvey, surveyContactSearch]);

  const resolveRequestedTaskDescription = async (candidates: string[]) => {
    for (const candidate of candidates) {
      try {
        const { data } = await axios.get("/proxy/api/requestedtasks/GetTaskByName", {
          params: {
            ordernumber: selectedOrderNumber,
            description: candidate
          }
        });

        if (!data) {
          continue;
        }

        if (typeof data === "string") {
          return data;
        }

        if (typeof data === "object") {
          return data.description || data.Description || candidate;
        }

        return candidate;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          continue;
        }

        throw error;
      }
    }

    return null;
  };

  const normalizeStatus = (value: unknown) => (typeof value === "string" ? value.trim().toLowerCase() : "");

  const onSave = async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const requestedTasks: Array<{ displayName: string; candidates: string[] }> = [];
      if (includeSurvey) {
        requestedTasks.push({ displayName: "Survey", candidates: ["Survey", "survey"] });
      }

      if (includeZoningReport) {
        requestedTasks.push({
          displayName: "Zoning Report",
          candidates: ["Zoning Report", "zoning report"]
        });
      }

      for (const requestedTask of requestedTasks) {
        const resolvedTaskDescription = await resolveRequestedTaskDescription(requestedTask.candidates);
        if (!resolvedTaskDescription) {
          throw new Error(
            `Requested task "${requestedTask.displayName}" was not found in Select for order ${selectedOrderNumber}.`
          );
        }

        await axios.post("/proxy/api/requestedtasks/update", {
          orderNumber: selectedOrderNumber,
          task: {
            description: resolvedTaskDescription,
            status: "Required"
          }
        });

        const { data: updatedTask } = await axios.get("/proxy/api/requestedtasks/GetTaskByName", {
          params: {
            ordernumber: selectedOrderNumber,
            description: resolvedTaskDescription
          }
        });

        const updatedStatus =
          typeof updatedTask === "object" && updatedTask
            ? updatedTask.status || updatedTask.Status || ""
            : "";

        if (normalizeStatus(updatedStatus) !== "required") {
          throw new Error(
            `Select returned "${updatedStatus || "blank"}" for ${resolvedTaskDescription} after the update attempt.`
          );
        }
      }

      if (includeSurvey && surveyContacts.length) {
        if (!user) {
          throw new Error("Unable to determine the current user.");
        }

        const { data } = await axios.get<Array<{ code?: string | null; lookupCode?: string | null; type?: string | null; contactType?: string | null }>>(
          `/proxy/api/orders/GetOrderContacts?orderNumber=${encodeURIComponent(selectedOrderNumber)}`
        );
        const existingSurveyContacts = Array.isArray(data)
          ? data.filter((contact) => contact.type === "Surveyor" || contact.contactType === "Surveyor")
          : [];

        for (const contact of surveyContacts) {
          const alreadyAdded = existingSurveyContacts.some(
            (existingContact) =>
              !!existingContact.lookupCode &&
              !!contact.lookupCode &&
              existingContact.lookupCode.toLowerCase() === contact.lookupCode.toLowerCase()
          );

          if (!alreadyAdded) {
            const contactName = contact.company?.trim() || contact.name?.trim() ||
              [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ").trim();
            const email = contact.emailsSplit?.filter(Boolean).join(";") || contact.email;

            await axios.post("/proxy/api/orders/addOrderContact", {
              orderNumber: selectedOrderNumber,
              contact: {
                lookupCode: contact.lookupCode,
                name: contact.name?.trim() || contactName,
                phone: contact.phone,
                company: contact.company?.trim() || contactName,
                email,
                payeeName: contact.payeeName?.trim() || contact.company?.trim() || contactName,
                type: "Surveyor",
                contactType: "Surveyor",
                isOther: false
              }
            });
            existingSurveyContacts.push({ lookupCode: contact.lookupCode, type: "Surveyor" });
          }

          await axios.post("/proxy/api/orders/AddNoteToRequestedTask", {
            orderNumber: selectedOrderNumber,
            taskDescription: "Survey",
            text: prependUserToSelectNote(`Survey requested from "${contact.company?.trim() || contact.name?.trim() || "Unknown contact"}"`, user.name)
          });
        }
      }

      await onSaved();
      closeModal();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(
          typeof error.response?.data === "string"
            ? error.response.data
            : error.response?.data?.message || "Unable to add this order right now."
        );
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("Unable to add this order right now.");
      }
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Survey / Zoning Order</DialogTitle>
      <DialogContent>
        <Box marginTop={2}>
          <Stack spacing={2}>
            <DebouncedOrderSearch
              value={selectedOrderNumber}
              textboxMargin="dense"
              onSelect={(_, option) => setSelectedOrderNumber(option || "")}
              disabled={isSubmitting}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeSurvey}
                  onChange={(e) => {
                    setIncludeSurvey(e.target.checked);
                    if (!e.target.checked) setSurveyContacts([]);
                  }}
                  disabled={isSubmitting}
                />
              }
              label="Survey"
            />
            {includeSurvey && (
              <Autocomplete
                multiple
                size="small"
                options={surveyContactOptions}
                value={surveyContacts}
                loading={isLoadingSurveyContacts}
                filterSelectedOptions
                getOptionLabel={(option) => option.name || option.company || ""}
                isOptionEqualToValue={(option, value) => option.lookupCode === value.lookupCode}
                onChange={(_, value) => setSurveyContacts(value)}
                inputValue={surveyContactSearch}
                onInputChange={(_, value) => setSurveyContactSearch(value)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.lookupCode || option.name} label={option.name || option.company} />
                  ))
                }
                renderOption={(props, option) => <ContactOption props={props} option={option} />}
                renderInput={(params) => (
                  <TextField {...params} label="Survey contacts" placeholder="Search surveyors" />
                )}
                disabled={isSubmitting}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox checked={includeZoningReport} onChange={(e) => setIncludeZoningReport(e.target.checked)} />
              }
              label="Zoning Report"
            />
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={isSaveDisabled}>
          {isSubmitting ? "Saving..." : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
